// Tokenized public-share for case reports.
//
// Agent side: POST /agent/cases/{caseId}/report/share → returns the
// live token (server idempotent — repeat clicks of "Copy share link"
// return the same token until it's revoked).
//
// Public side: GET /public/report/{token} → returns the persisted
// SellerReport / BuyerReport payload. No auth — security is the
// 128-bit token in the URL.
//
// The agent endpoint returns just the token; the FE composes the
// absolute URL from `window.location.origin` so the backend doesn't
// need to know its own public host.

import { apiDelete, apiJson, apiPostJson } from "./api";
import type { BuyerReport, SellerReport } from "./proagentApi";

export interface ReportShare {
  token: string;
  report_type: "seller" | "buyer";
  created_at: string;
}

export async function createOrFetchShare(caseId: string): Promise<ReportShare> {
  return apiPostJson<ReportShare>(`/agent/cases/${caseId}/report/share`, {});
}

// buildShareUrl returns the absolute URL the recipient will open.
// Kept here so the route shape and the agent-endpoint contract live
// in one file — if either side changes, the bug surfaces in the
// neighbouring function.
export function buildShareUrl(token: string): string {
  return `${window.location.origin}/pro/share/${token}`;
}

// fetchPublicReport hits the unauthenticated reader. The response
// shape is one of the report unions — the caller narrows on
// `report_type` (which the backend includes in every payload).
export async function fetchPublicReport(
  token: string,
): Promise<SellerReport | BuyerReport> {
  return apiJson<SellerReport | BuyerReport>(`/public/report/${token}`);
}

// revokeShare invalidates the live share token for a case. Backend
// returns 204 on success, 404 when no live token exists. After
// revoke, calling createOrFetchShare for the same case mints a
// fresh token; old links 404 on the public reader.
export async function revokeShare(caseId: string): Promise<void> {
  return apiDelete(`/agent/cases/${caseId}/report/share`);
}
